import { type ShareProvider, type ShareProviderMeta, type ShareUrlInfo } from './ContentItemEditor.types';

/*
    The share link for one provider, ported from the Angular Navigation.ShareService so this page
    needs no injector to build it.

    Only the three web providers the editor offers. The Angular service also handled email and the
    Cordova social-sharing plugin, neither of which reaches a web-only editor page.
*/
const PROVIDER_META: Record<ShareProvider, ShareProviderMeta> = {
    facebook: {
        serviceUrl: 'https://www.facebook.com/sharer/sharer.php',
        urlField: 'u',
        titleField: 't',
    },

    linkedin: {
        serviceUrl: 'http://www.linkedin.com/shareArticle',
        urlField: 'url',
        titleField: 'title',
        descriptionField: 'text',
        sourceField: 'source',
        additionalParams: { mini: 'true' },
    },

    twitter: {
        serviceUrl: 'https://twitter.com/intent/tweet',
        urlField: 'url',
        sourceField: 'source',
        descriptionField: 'text',
    },
};

// The tracking parameters the Angular service appended to the shared URL itself, so the visit is
// attributed to the share rather than to the page it lands on.
function withCampaignParams(url: string, provider: ShareProvider, info: ShareUrlInfo) {
    if (!info.campaignDescription && !info.campaignMedium) return url;

    const params = new URLSearchParams({ utm_source: provider });
    if (info.campaignMedium) params.set('utm_medium', info.campaignMedium);
    if (info.campaignDescription) params.set('utm_campaign', info.campaignDescription);
    if (info.campaignContent) params.set('utm_content', info.campaignContent);

    return `${url}${url.includes('?') ? '&' : '?'}${params}`;
}

export function buildShareUrl(provider: ShareProvider, info: ShareUrlInfo): string {
    const meta = PROVIDER_META[provider];

    // A provider may override the title or the description: a tweet carries the tweet template
    // rather than the SEO description.
    const details = { ...info, ...info[provider] };

    if (!details.url || !details.title) {
        throw new Error('A share needs at least a URL and a title');
    }

    const url = withCampaignParams(details.url, provider, details);
    const params = new URLSearchParams();

    /*
        Each field only when the provider has one. The Angular service wrote the title field
        unconditionally, so a tweet, which has no title field, came out with a literal
        'undefined=' parameter in front of it.
    */
    if (meta.titleField) params.set(meta.titleField, details.title);
    params.set(meta.urlField, url);
    if (meta.sourceField) params.set(meta.sourceField, url);
    if (meta.descriptionField && details.description) params.set(meta.descriptionField, details.description);
    Object.entries(meta.additionalParams || {}).forEach(([key, value]) => params.set(key, value));

    return `${meta.serviceUrl}?${params}`;
}

/*
    A small centred popup, as the Angular service sized it. Cordova took a different path, which a
    web-only editor page never reaches.
*/
export function shareWindowFeatures(): string {
    const height = 350;
    const width = 450;
    const left = window.screen.width / 2 - (width / 2 + 10);
    const top = window.screen.height / 2 - (height / 2 + 50);

    return `location=no,toolbar=no,status=no,height=${height},width=${width},resizable=yes,left=${left},top=${top},screenX=${left},screenY=${top},menubar=no,scrollbars=no,directories=no`;
}
